// Custom hook: all useEffect hooks for ProductionOrder
import { useEffect } from "react";
import { modifiedWithProperPrecision } from "./productionUtils";
import {
  savingTotalSalePrice,
  savingOtherChargesData,
  savingRemarks,
  savingQuantityInput,
  savingSearchedProductsData,
} from "./productionStorage";
import { shortcutData } from "./columnDefinitions";

const useProductionEffects = ({
  // State values
  isWeightPopupVisible,
  selectedProduct,
  setDisableSearch,
  disableSearch,
  otherCharges,
  firstInputRef,
  rightRef,
  keyBoardInputValue,
  qtyRef,
  selectedQuantity,
  QtyShowInputFields,
  inputRef,
  showInputFields,
  qtyInputRef,
  totalQtyRef,
  showRemarks,
  textAreaRef,
  buttonRef,
  chooseProduct,
  searchedProductsData,
  shortcutsListModalOpen,
  showOrderHistory,
  setCurrentDateTime,
  setShowAltUnderline,
  reverseOrderData,
  pricePrecision,
  setOtherChargesData,
  setSelectedProduct,
  setTotalSalePrice,
  setFormattedDate,
  setRemarks,
  setQuantityInputValue,
  setTempQuantityInputValue,
  setSearchedProductsData,
  updateProductWithUOMData,
  modifiedData,
  updateCartTotals,
  selectedProductSalePrice,
  subtotal,
  otherChargesData,
  pricingMethod,
  quantityInputValue,
  setTotalWeights,
  tempOtherChargesData,
  setTempOtherChargesData,
  // Shortcuts keyboard nav
  selectedRowIndex,
  setSelectedRowIndex,
  handleChooseProduct,
  inputSearchRef,
  hasReverseOrderData,
  totalSalePrice,
  setShowOrderHistory,
  handleOtherChanges,
  remarks,
  setShowRemarks,
  setTempRemarks,
  handleProceed,
  handleCancel,
  setIsLocked,
  setShortcutsListModalOpen,
  // Scroll helper
  shortcutsRef,
  scrollToRow,
  // Choose product focus
  plusButtonRef,
}) => {
  // Clock
  useEffect(() => {
    const interval = setInterval(() => setCurrentDateTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Focus effects
  useEffect(() => {
    if (isWeightPopupVisible) {
      setDisableSearch(true);
    } else if (selectedProduct) {
      setDisableSearch(false);
    } else {
      setDisableSearch(true);
    }
  }, [selectedProduct, disableSearch, isWeightPopupVisible]);

  useEffect(() => {
    if (otherCharges) {
      setTimeout(() => {
        if (firstInputRef.current) {
          firstInputRef.current.focus();
          firstInputRef.current.select();
        }
      }, 0);
    }
  }, [otherCharges]);

  useEffect(() => {
    if (rightRef.current) rightRef.current.focus();
  }, [keyBoardInputValue]);

  useEffect(() => {
    if (qtyRef.current) qtyRef.current.focus();
  }, [selectedQuantity]);

  useEffect(() => {
    if (QtyShowInputFields && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [QtyShowInputFields]);

  useEffect(() => {
    if (showInputFields) {
      requestAnimationFrame(() => {
        qtyInputRef.current?.focus();
        qtyInputRef.current?.select();
        totalQtyRef.current?.focus();
        totalQtyRef.current?.select();
      });
    }
  }, [showInputFields]);

  useEffect(() => {
    if (showRemarks && textAreaRef.current) {
      setTimeout(() => textAreaRef.current.focus(), 0);
    }
    const amountInput = document.getElementById("sm-notes-input");
    if (amountInput) amountInput?.focus();
  }, [showRemarks]);

  useEffect(() => {
    if (buttonRef.current && !chooseProduct && !shortcutsListModalOpen && !showOrderHistory) {
      buttonRef.current.focus();
    }
  }, [chooseProduct, searchedProductsData, shortcutsListModalOpen, showOrderHistory]);

  // Alt underline effect
  useEffect(() => {
    const handleKeyDown = (e) => { if (e.altKey) setShowAltUnderline(true); };
    const handleKeyUp = (e) => { if (!e.altKey) setShowAltUnderline(false); };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  // Reverse order data effect
  useEffect(() => {
    const run = async () => {
      if (reverseOrderData?.length > 0) {
        if (reverseOrderData[0]?.indirectCost !== null) {
          let tempArray = [];
          reverseOrderData[0]?.indirectCost.forEach((item) => {
            tempArray.push({ name: item?.prIndirectcostName, weight: item?.value, pr_indirectcost_id: item?.prIndirectcostId });
          });
          setOtherChargesData(tempArray);
        }
        setSelectedProduct({ name: reverseOrderData[0]?.productName, m_product_id: reverseOrderData[0]?.mProductId, value: reverseOrderData[0]?.productValue });
        setTotalSalePrice(reverseOrderData[0]?.salePrice);
        const currentDate = new Date();
        const fDate = currentDate.getDate().toString().padStart(2, "0") + "/" + (currentDate.getMonth() + 1).toString().padStart(2, "0") + "/" + currentDate.getFullYear();
        setFormattedDate(fDate);
        setRemarks(reverseOrderData[0]?.description);
        setQuantityInputValue(reverseOrderData[0]?.qtyToReverse);
        setTempQuantityInputValue(reverseOrderData[0]?.qtyToReverse);
        let tempArray = [];
        reverseOrderData[0]?.ioproducts?.forEach((product, index) => {
          tempArray.push({
            key: index,
            cs_uom_id: product.csUomId,
            uomname: product.uom,
            product_name: product.product,
            product_value: product.productValue,
            m_product_id: product.mProductId,
            ediCode: product.ediCode,
            weight: parseFloat(
              (((product.actualqty / reverseOrderData[0]?.qtyproduction) * reverseOrderData[0]?.qtyToReverse) / reverseOrderData[0]?.qtyToReverse).toFixed(
                product?.uomData[0]?.stdprecision
              )
            ),
            totalWeight: parseFloat(((product.actualqty / reverseOrderData[0]?.qtyproduction) * reverseOrderData[0]?.qtyToReverse).toFixed(product?.uomData[0]?.stdprecision)),
            stock: product?.stockOnHand !== null ? product.stockOnHand : 0,
            sale_price: parseFloat(product.salePrice.toFixed(pricePrecision)),
            total: parseFloat(((product.totalPrice / reverseOrderData[0]?.qtyproduction) * reverseOrderData[0]?.qtyToReverse).toFixed(product?.uomData[0]?.stdprecision)),
          });
        });
        const modifiedArr = await Promise.all(tempArray?.map((product) => updateProductWithUOMData(product)));
        let finalData = modifiedData(modifiedArr);
        updateCartTotals(finalData);
        setSearchedProductsData(finalData);
      }
    };
    run();
  }, [reverseOrderData]);

  // Total sale price effect
  useEffect(() => {
    if (reverseOrderData?.length === 0) {
      const totalWeightsCalc = otherChargesData?.reduce((acc, item) => acc + (parseFloat(item.weight) || 0), 0) || 0;
      let price = pricingMethod === "Standard Price" ? selectedProductSalePrice : selectedProductSalePrice * quantityInputValue + subtotal;
      const finalPrice = pricingMethod === "Standard Price" ? price : quantityInputValue !== "0" && quantityInputValue !== 0 ? price / quantityInputValue : 0;
      setTotalSalePrice(finalPrice);
      setTotalWeights(totalWeightsCalc);
      savingTotalSalePrice(finalPrice);
    }
  }, [selectedProductSalePrice, subtotal, otherChargesData, pricingMethod, quantityInputValue]);

  // Temp other charges sync
  useEffect(() => {
    if (otherCharges) setTempOtherChargesData([...otherChargesData]);
  }, [otherCharges, otherChargesData]);

  // Choose product focus
  useEffect(() => {
    if (chooseProduct) {
      const timer = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
  }, [chooseProduct]);

  // Shortcuts modal keyboard nav
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!shortcutsListModalOpen || chooseProduct) return;
      const { key } = e;

      if (key === "Enter") {
        e.preventDefault();
        if (selectedRowIndex === null) return;
        if (selectedRowIndex === 0) { handleChooseProduct(); setShortcutsListModalOpen(false); }
        else if (selectedRowIndex === 5) {
          setShortcutsListModalOpen(false);
          setTimeout(() => { if (inputSearchRef?.current && !hasReverseOrderData && !disableSearch) inputSearchRef.current.focus(); }, 300);
        }
        else if (selectedRowIndex === 6) { if (totalSalePrice <= 0) setShowOrderHistory(true); setShortcutsListModalOpen(false); }
        else if (selectedRowIndex === 1 && selectedProduct !== null) { handleOtherChanges(); setShortcutsListModalOpen(false); }
        else if (selectedRowIndex === 2 && selectedProduct !== null) { setShowRemarks(true); setTempRemarks(remarks); setShortcutsListModalOpen(false); }
        else if (selectedRowIndex === 3 && selectedProduct !== null) { handleProceed(); setShortcutsListModalOpen(false); }
        else if (selectedRowIndex === 4) { handleCancel(); setShortcutsListModalOpen(false); }
        else if (selectedRowIndex === 8) { setIsLocked(true); setShortcutsListModalOpen(false); }
      }

      if (key === "ArrowDown") {
        e.preventDefault();
        setSelectedRowIndex((prev) => { const nextIndex = Math.min(prev + 1, shortcutData.length - 1); scrollToRow(nextIndex); return nextIndex; });
      }
      if (key === "ArrowUp") {
        e.preventDefault();
        setSelectedRowIndex((prev) => { const nextIndex = Math.max(prev - 1, 0); scrollToRow(nextIndex); return nextIndex; });
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [shortcutsListModalOpen, selectedRowIndex]);
};

export default useProductionEffects;
